import * as ENGINE from '@gnsx/genesys.js';

export interface ActorDependencyReloadTarget {
  uuid: string;
  asExportedObject: () => ENGINE.SerializedData;
  getPrefabName: () => string | null | undefined;
}

export interface DependencyReference {
  sourceAssetPath: string;
  sourceAssetType: string;
  referencePath?: string;
  referenceType?: string;
  location?: string;
}

export interface ReloadDependentActorsByAssetOptions {
  actors: ActorDependencyReloadTarget[] | undefined;
  reloadActor: (actor: ActorDependencyReloadTarget, snapshot?: ENGINE.SerializedData) => Promise<boolean>;
  updateOutliner?: () => void;
  findReferences?: (assetPath: string) => Promise<DependencyReference[]>;
  scanAssetPathReferences?: typeof ENGINE.WorldSerializer.scanAssetPathReferences;
}

interface ActorReloadCandidate {
  actor: ActorDependencyReloadTarget;
  snapshot?: ENGINE.SerializedData;
}

function getDependentPrefabPathsFromReferences(
  references: DependencyReference[]
): Set<string> {
  const dependentPrefabPaths = new Set<string>();
  for (const reference of references) {
    if (reference.sourceAssetType === 'prefab') {
      dependentPrefabPaths.add(reference.sourceAssetPath);
    }
  }
  return dependentPrefabPaths;
}

export async function reloadDependentActorsByAsset(
  assetPath: string,
  options: ReloadDependentActorsByAssetOptions
): Promise<number> {
  const actors = options.actors;
  if (!actors || actors.length === 0) {
    return 0;
  }

  const findReferences = options.findReferences ?? (async () => []);
  const scanAssetPathReferences = options.scanAssetPathReferences ?? ENGINE.WorldSerializer.scanAssetPathReferences;

  let dependentPrefabPaths = new Set<string>();
  try {
    const references = await findReferences(assetPath);
    dependentPrefabPaths = getDependentPrefabPathsFromReferences(references);
  } catch (error) {
    console.warn('[sceneActions.reloadActorsReferencingAsset] Failed to analyze dependency references', { assetPath, error });
  }

  const candidateActors = new Map<string, ActorReloadCandidate>();

  for (const actor of actors) {
    try {
      const snapshot = actor.asExportedObject();
      const hasDirectReference = scanAssetPathReferences(snapshot, assetPath).length > 0;
      const prefabName = actor.getPrefabName();
      const hasPrefabDependency = prefabName ? dependentPrefabPaths.has(prefabName) : false;

      if (hasDirectReference || hasPrefabDependency) {
        candidateActors.set(actor.uuid, { actor, snapshot });
      }
    } catch (error) {
      try {
        const prefabName = actor.getPrefabName();
        if (prefabName && dependentPrefabPaths.has(prefabName)) {
          candidateActors.set(actor.uuid, { actor });
        }
      } catch (prefabError) {
        console.warn('[sceneActions.reloadActorsReferencingAsset] Failed to read actor prefab dependency', {
          actorUuid: actor.uuid,
          error: prefabError
        });
      }

      console.warn('[sceneActions.reloadActorsReferencingAsset] Failed to inspect actor dependency snapshot', {
        actorUuid: actor.uuid,
        assetPath,
        error
      });
    }
  }

  let reloadedCount = 0;
  for (const candidate of candidateActors.values()) {
    try {
      const success = await options.reloadActor(candidate.actor, candidate.snapshot);
      if (success) {
        reloadedCount++;
      }
    } catch (error) {
      console.warn('[sceneActions.reloadActorsReferencingAsset] Failed to reload dependent actor', {
        actorUuid: candidate.actor.uuid,
        assetPath,
        error
      });
    }
  }

  if (reloadedCount > 0) {
    options.updateOutliner?.();
  }

  return reloadedCount;
}
